import { useEffect, useState } from 'react';
import ButtonComponent from '../../components/button/button';
import dayjs, { Dayjs } from 'dayjs';
import { getSearchResults } from '../../features/search/service/search-service';
import DatePickerComponent from '../../components/input/input-date';
import { ExportDelayData } from './mapper/delay-mapper';
import { getMe } from '../../features/search/service/user-service';
import { getRoleById } from '../../features/search/service/role-service';
import {
    getCompanyById,
    getSuperAdminCompaniesDashboard,
    getSuperAdminCompanyById
} from '../../features/search/service/company-service';
import { User } from '../../shared/types/user';
import { Role } from '../../shared/types/role';
import { Company } from '../../shared/types/company';
import { SelectOption } from '../../shared/types/select-options';
import SelectComponent from '../../components/input/select';
import { useTranslation } from '../../shared/i18n/i18n-context';
import { MenuTop } from '../../components/menu/top/menu-top';
import { PageBreadcrumb } from '../../components/page-breadcrumb/page-breadcrumb';
import { ProjectLoader } from '../../components/project-loader/project-loader';
import './styles.css';

const Reports = () => {
    const { language, t } = useTranslation('reports');
    const { t: menuT } = useTranslation('menu');
    const [initialDate, setInitialDate] = useState<Dayjs | null>(null);
    const [finalDate, setFinalDate] = useState<Dayjs | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isFillingPeriod, setIsFillingPeriod] = useState(false);
    const [user, setUser] = useState<User | null>(null);
    const [userRole, setUserRole] = useState<Role | null>(null);
    const [companies, setCompanies] = useState<SelectOption[]>([]);
    const [companyDetails, setCompanyDetails] = useState<Company[]>([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
    const [loadingData, setLoadingData] = useState(true);

    const isSuperAdmin = userRole?.name === 'super-admin';

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoadingData(true);
                
                const userData = await getMe();
                setUser(userData);

                // Buscar a role do usuário
                let roleData: Role | null = null;
                if (userData.role_id) {
                    roleData = await getRoleById(userData.role_id);
                    setUserRole(roleData);
                }

                // Se for super-admin, buscar todas as empresas
                if (roleData?.name === 'super-admin') {
                    const companiesResponse = await getSuperAdminCompaniesDashboard();
                    const companiesData = companiesResponse.data;
                    setCompanyDetails(companiesData);
                    setCompanies(companiesData.map((company) => ({
                        id: company.id,
                        label: company.name || company.description,
                    })));
                } else {
                    // Se não for super-admin, usar a empresa do usuário
                    const companyId = userData.company?.id || userData.companyId;
                    if (companyId) {
                        setSelectedCompanyId(companyId);
                    }
                }

            } catch (error) {
                console.error('❌ Erro ao buscar dados:', error);
                alert(t('loadError'));
            } finally {
                setLoadingData(false);
            }
        };
        fetchData();
    }, [t]);

    const getCompanyCreatedAt = (company?: Company | User['company'] | null) => {
        return company?.created_at || company?.createdAt;
    };

    const getSelectedCompanyCreatedAt = async () => {
        if (isSuperAdmin) {
            const selectedCompany = companyDetails.find((company) => String(company.id) === selectedCompanyId);
            const selectedCompanyCreatedAt = getCompanyCreatedAt(selectedCompany);

            if (selectedCompanyCreatedAt) {
                return selectedCompanyCreatedAt;
            }

            const company = await getSuperAdminCompanyById(selectedCompanyId);
            return getCompanyCreatedAt(company);
        }

        const userCompanyCreatedAt = getCompanyCreatedAt(user?.company);

        if (userCompanyCreatedAt) {
            return userCompanyCreatedAt;
        }

        const company = await getCompanyById(selectedCompanyId);
        return getCompanyCreatedAt(company);
    };

    const handleFillFullPeriod = async () => {
        if (!selectedCompanyId) {
            alert(t('selectCompanyFullPeriod'));
            return;
        }

        try {
            setIsFillingPeriod(true);

            const companyCreatedAt = await getSelectedCompanyCreatedAt();

            if (!companyCreatedAt) {
                alert(t('companyCreationDateMissing'));
                return;
            }

            const companyCreatedDate = dayjs(companyCreatedAt);

            if (!companyCreatedDate.isValid()) {
                alert(t('companyCreationDateInvalid'));
                return;
            }

            setInitialDate(companyCreatedDate.startOf('day'));
            setFinalDate(dayjs());
        } catch (error) {
            alert(t('fillFullPeriodError'));
        } finally {
            setIsFillingPeriod(false);
        }
    };

    const handleCompanyChange = (value: string | number) => {
        setSelectedCompanyId(String(value));
        setInitialDate(null);
        setFinalDate(null);
    };

    const handleInitialDateChange = (value: Dayjs | null) => {
        setInitialDate(value);

        if (!value || (finalDate && finalDate.isBefore(value, 'day'))) {
            setFinalDate(null);
        }
    };

    const handleFinalDateChange = (value: Dayjs | null) => {
        setFinalDate(value);
    };

    const handleDownloadReport = async () => {
        if (!selectedCompanyId) {
            console.error('❌ Nenhuma empresa selecionada');
            alert(t('selectCompanyReport'));
            return;
        }

        try {
            setIsLoading(true);
            
            const queryParams = new URLSearchParams();
            queryParams.append('companyId', selectedCompanyId);
            
            if (initialDate) {
                const formattedStart = initialDate.format('YYYY-MM-DD');
                queryParams.append('startDate', formattedStart);
            }
            
            if (finalDate) {
                const formattedEnd = finalDate.format('YYYY-MM-DD');
                queryParams.append('endDate', formattedEnd);
            }
            
            const response = await getSearchResults(queryParams);
            
            if (response.data && response.data.length > 0) {
                // Buscar nome da empresa para o arquivo
                let companyName = t('defaultCompanyFileName');
                
                if (isSuperAdmin) {
                    const selectedCompany = companies.find(c => String(c.id) === selectedCompanyId);
                    companyName = selectedCompany?.label || t('defaultCompanyFileName');
                } else {
                    companyName = user?.company?.name || t('defaultCompanyFileName');
                }
                
                const safeCompanyName = companyName.replace(/\s+/g, '_').toLowerCase();
                const fileName = `${t('reportFilePrefix')}_${safeCompanyName}`;
                
                ExportDelayData(response, fileName, language);
                
                alert(`✅ ${t('reportSuccess')}\n${response.data.length} ${t('recordsFound')}`);
            } else {
                alert(t('noRecordsFound'));
            }
        } catch (error) {
            alert(t('reportError'));
        } finally {
            setIsLoading(false);
        }
    };

    const selectedCompanyLabel = isSuperAdmin
        ? companies.find((company) => String(company.id) === selectedCompanyId)?.label
        : user?.company?.name || user?.company?.description;

    const isCompanySelected = Boolean(selectedCompanyId);
    const isStartDateEnabled = isCompanySelected;
    const isFinalDateEnabled = Boolean(initialDate);

    const formatSelectedDate = (date: Dayjs | null) => {
        return date ? date.format(language === 'en' ? 'MM/DD/YYYY' : 'DD/MM/YYYY') : t('notSelected');
    };

    const getStepClassName = (isSelected: boolean, isEnabled = true) => {
        return [
            'reports-timeline-step',
            isSelected ? 'is-selected' : isEnabled ? 'is-ready' : 'is-muted',
            isEnabled ? '' : 'is-locked'
        ].filter(Boolean).join(' ');
    };

    if (loadingData) {
        return (
            <>
                <MenuTop />
                <div className="reports-page reports-page-loading">
                    <ProjectLoader label={t('loading')} />
                </div>
            </>
        );
    }
    
    return (
        <>
            <MenuTop />
            <main className="reports-page">
                <header className="reports-header">
                    <PageBreadcrumb
                        items={[
                            { label: menuT('home'), to: '/home' },
                            { label: menuT('generateReport') }
                        ]}
                    />
                    <h1>{t('selectCompanyAndPeriod')}</h1>
                    <p>{t('timelineSubtitle')}</p>
                </header>

                <section className="reports-timeline" aria-label={t('timelineSubtitle')}>
                    <article className={getStepClassName(isCompanySelected)}>
                        <div className="reports-timeline-marker">1</div>
                        <div className="reports-timeline-content">
                            <span className="reports-timeline-label">{t('company')}</span>
                            <strong>{selectedCompanyLabel || t('notSelected')}</strong>
                            <div className="reports-timeline-control">
                                {isSuperAdmin && companies.length > 0 ? (
                                    <SelectComponent
                                        label={t('company')}
                                        value={selectedCompanyId}
                                        onChange={handleCompanyChange}
                                        options={companies}
                                    />
                                ) : (
                                    <div className="reports-readonly-filter">
                                        {selectedCompanyLabel || t('notSelected')}
                                    </div>
                                )}
                            </div>
                        </div>
                    </article>

                    <article className={getStepClassName(Boolean(initialDate), isStartDateEnabled)}>
                        <div className="reports-timeline-marker">2</div>
                        <div className="reports-timeline-content">
                            <span className="reports-timeline-label">{t('startDate')}</span>
                            <strong>{formatSelectedDate(initialDate)}</strong>
                            <div className="reports-timeline-control">
                                <DatePickerComponent
                                    label={t('startDate')}
                                    value={initialDate}
                                    onChange={handleInitialDateChange}
                                    disabled={!isStartDateEnabled}
                                />
                            </div>
                        </div>
                    </article>

                    <article className={getStepClassName(Boolean(finalDate), isFinalDateEnabled)}>
                        <div className="reports-timeline-marker">3</div>
                        <div className="reports-timeline-content">
                            <span className="reports-timeline-label">{t('endDate')}</span>
                            <strong>{formatSelectedDate(finalDate)}</strong>
                            <div className="reports-timeline-control">
                                <DatePickerComponent
                                    label={t('endDate')}
                                    value={finalDate}
                                    onChange={handleFinalDateChange}
                                    disabled={!isFinalDateEnabled}
                                />
                            </div>
                        </div>
                    </article>
                </section>

                <div className="reports-actions">
                    <ButtonComponent
                        label={isFillingPeriod ? t('loadingPeriod') : t('fullPeriod')}
                        onClick={() => handleFillFullPeriod()}
                        props={{
                            disabled: isLoading || isFillingPeriod || !selectedCompanyId,
                        }}
                    />

                    <ButtonComponent
                        label={isLoading ? t('generating') : t('downloadReport')}
                        onClick={() => handleDownloadReport()}
                        props={{
                            disabled: !initialDate || !finalDate || isLoading || isFillingPeriod || !selectedCompanyId,
                        }}
                    />
                </div>
            </main>
        </>
    );
}

export default Reports;
